import React, { useState } from 'react';
import { Delete, Wifi, WifiOff, CreditCard } from 'lucide-react';
import { money, pct } from './helpers';

const TIPS = [15, 18, 20];

const ChargeScreen = ({ seller, feeRate, online, busy, onCard }) => {
  const [cents, setCents] = useState(0);
  const [tipMode, setTipMode] = useState('none'); // 'none' | 15|18|20 | 'custom'
  const [customTip, setCustomTip] = useState('');

  const amount = cents / 100;
  const taxRate = Number(seller?.tax_rate || 0);
  const tax = Math.round(amount * taxRate * 100) / 100;
  const tipBase = amount + tax;
  let tip = 0;
  if (tipMode === 'custom') tip = Math.round((parseFloat(customTip) || 0) * 100) / 100;
  else if (tipMode !== 'none') tip = Math.round(tipBase * (tipMode / 100) * 100) / 100;

  const subtotal = Math.round((amount + tax + tip) * 100) / 100;
  const STRIPE_PCT = 0.029, STRIPE_FIXED = 0.30;
  const fee = Math.round(subtotal * feeRate * 100) / 100;            // FLIX platform fee (1.5%)
  const total = subtotal > 0 ? Math.round(((subtotal + fee + STRIPE_FIXED) / (1 - STRIPE_PCT)) * 100) / 100 : 0;
  const processingFee = subtotal > 0 ? Math.round((total - subtotal - fee) * 100) / 100 : 0; // Stripe fee (paid by customer)
  const amountStr = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const press = (d) => setCents((c) => Math.min(c * 10 + d, 9999999));
  const backspace = () => setCents((c) => Math.floor(c / 10));

  const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div className="flex flex-col min-h-[calc(100vh-110px)] px-5 pt-6">
      <div className="flex items-center justify-between">
        <span className="brand text-2xl">flix<span className="text-primary">.</span></span>
        <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${online ? 'bg-secondary text-muted-foreground' : 'bg-destructive/15 text-destructive'}`} data-testid="connection-status">
          {online ? <Wifi size={13} strokeWidth={2.75} /> : <WifiOff size={13} strokeWidth={2.75} />}
          {online ? 'Online' : 'Offline'}
        </div>
      </div>

      <div className="text-center mt-6">
        <p className="font-heading text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Enter amount</p>
        <p data-testid="charge-amount" className={`font-heading font-black tracking-tighter leading-none mt-2 ${amount > 0 ? 'text-primary' : 'text-muted-foreground/40'}`} style={{ fontSize: 'clamp(3rem, 18vw, 5rem)' }}>
          ${amountStr}
        </p>
      </div>

      {/* Tip selector */}
      <div className="mt-5">
        <p className="font-heading text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Add tip</p>
        <div className="flex gap-2">
          <button data-testid="tip-chip-none" onClick={() => setTipMode('none')} className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${tipMode === 'none' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>None</button>
          {TIPS.map((t) => (
            <button key={t} data-testid={`tip-chip-${t}`} onClick={() => setTipMode(t)} className={`flex-1 py-2 rounded-full text-sm font-bold border transition-colors ${tipMode === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>{t}%</button>
          ))}
          <button data-testid="tip-chip-custom" onClick={() => setTipMode('custom')} className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${tipMode === 'custom' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>Custom</button>
        </div>
        {tipMode === 'custom' && (
          <input data-testid="tip-custom-input" type="number" min="0" step="0.5" value={customTip} onChange={(e) => setCustomTip(e.target.value)} placeholder="Custom tip $"
            className="mt-2 w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm outline-none focus:border-primary transition" />
        )}
      </div>

      {/* Breakdown */}
      {amount > 0 && (
        <div className="mt-4 rounded-2xl bg-card border border-border p-4 space-y-1.5">
          <Row label="Amount" value={money(amount)} />
          {taxRate > 0 && <Row label={`Tax (${pct(taxRate)})`} value={money(tax)} />}
          {tip > 0 && <Row label="Tip" value={money(tip)} />}
          {(taxRate > 0 || tip > 0) && <Row label="Subtotal" value={money(subtotal)} bold />}
          <Row label="Card Processing Fee" value={money(fee + processingFee)} muted />
          <div className="border-t border-dashed border-border my-1.5" />
          <div className="flex justify-between items-center">
            <span className="font-heading font-bold">Total Due</span>
            <span data-testid="charge-total" className="font-heading font-black text-lg text-primary">{money(total)}</span>
          </div>
          <p className="text-[11px] text-primary font-medium">✓ Merchant receives the full sale amount.</p>
        </div>
      )}

      {/* Dialpad */}
      <div className="grid grid-cols-3 gap-1 mt-auto pt-4">
        {keys.map((k) => (
          <button key={k} data-testid={`dialpad-key-${k}`} onClick={() => press(k)} className="min-h-[64px] text-2xl font-heading font-bold rounded-2xl active:bg-secondary active:scale-[0.97] transition-all duration-75">{k}</button>
        ))}
        <button data-testid="dialpad-key-00" onClick={() => { setCents((c) => Math.min(c * 100, 9999999)); }} className="min-h-[64px] text-2xl font-heading font-bold rounded-2xl active:bg-secondary active:scale-[0.97] transition-all duration-75">00</button>
        <button data-testid="dialpad-key-0" onClick={() => press(0)} className="min-h-[64px] text-2xl font-heading font-bold rounded-2xl active:bg-secondary active:scale-[0.97] transition-all duration-75">0</button>
        <button data-testid="dialpad-backspace" onClick={backspace} className="min-h-[64px] flex items-center justify-center rounded-2xl text-muted-foreground active:bg-secondary active:scale-[0.97] transition-all duration-75"><Delete size={24} strokeWidth={2.5} /></button>
      </div>

      {/* CTA */}
      <div className="mt-3">
        <button data-testid="charge-submit-button" disabled={busy || amount < 1 || !online} onClick={() => onCard({ amount, tax, tip })}
          className="w-full h-14 rounded-full bg-primary text-primary-foreground font-heading font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40">
          <CreditCard size={20} strokeWidth={2.75} /> {busy ? 'Processing…' : online ? 'Charge card' : 'Card needs internet'}
        </button>
      </div>
    </div>
  );
};

const Row = ({ label, value, muted, bold }) => (
  <div className="flex justify-between text-sm">
    <span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold text-foreground' : 'text-foreground/80'}>{label}</span>
    <span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold text-foreground' : 'text-foreground/80'}>{value}</span>
  </div>
);

export default ChargeScreen;
